import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { useAuth } from '../context/AuthContext'

const ENTITY_TYPES = ['1040', '1120S', '1065', '1120', 'schedule_c', '990']
const ENTITY_LABELS = { '1040': '1040 — Personal', '1120S': '1120S — S-Corp', '1065': '1065 — Partnership', '1120': '1120 — C-Corp', 'schedule_c': 'Schedule C — Sole Prop', '990': '990 — Nonprofit' }
function getDefaultDeadline(entityType) {
  const year = new Date().getFullYear()
  const deadlines = {
    '1040':       `${year}-04-15`,
    'schedule_c': `${year}-04-15`,
    '1120S':      `${year}-03-16`,
    '1065':       `${year}-03-16`,
    '1120':       `${year}-04-15`,
    '990':        `${year}-05-15`,
  }
  return deadlines[entityType] || `${year}-04-15`
}

const STATUSES = ['not_started', 'in_progress', 'review', 'filed', 'on_hold']
const STATUS_LABELS = { not_started: 'Not Started', in_progress: 'In Progress', review: 'In Review', filed: 'Filed', on_hold: 'On Hold' }
const STATUS_COLORS = { not_started: 'neutral', in_progress: 'info', review: 'warning', filed: 'success', on_hold: 'danger' }

function StatusBadge({ status }) {
  return <span className={`badge badge-${STATUS_COLORS[status] || 'neutral'}`}>{STATUS_LABELS[status] || status}</span>
}

function Modal({ title, onClose, children }) {
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}
      onClick={e => { if (e.target === e.currentTarget) onClose() }}>
      <div className="card animate-fadeUp" style={{ width: '100%', maxWidth: 560, maxHeight: '90vh', overflow: 'auto', padding: 0 }}>
        <div style={{ padding: '20px 24px', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h2 style={{ fontSize: 18 }}>{title}</h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 20, color: '#62615D', cursor: 'pointer', lineHeight: 1 }}>×</button>
        </div>
        <div style={{ padding: '24px' }}>{children}</div>
      </div>
    </div>
  )
}

function AddClientForm({ onSave, onClose }) {
  const { profile } = useAuth()
  const [form, setForm] = useState({
    full_name: '', business_name: '', email: '', phone: '',
    entity_type: '1040', status: 'not_started',
    tax_year: new Date().getFullYear(), deadline: getDefaultDeadline('1040'), notes: ''
  })
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }))
  const setEntityType = e => setForm(f => ({ ...f, entity_type: e.target.value, deadline: getDefaultDeadline(e.target.value) }))

  async function handleSubmit(e) {
    e.preventDefault()
    setError(''); setLoading(true)
    const { data, error } = await supabase.from('clients').insert({
      ...form,
      cpa_id: profile.id,
      deadline: form.deadline || null,
    }).select().single()
    if (error) { setError(error.message); setLoading(false); return }

    // Create organizer record for this client
    await supabase.from('organizers').insert({
      client_id: data.id,
      tax_year: form.tax_year,
      entity_type: form.entity_type,
      status: 'not_started'
    })

    onSave(data)
    setLoading(false)
  }

  return (
    <form onSubmit={handleSubmit}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
        <div style={{ gridColumn: '1 / -1' }}>
          <label className="label">Business / Client Name *</label>
          <input className="input" placeholder="Rivera's Bakery" value={form.business_name} onChange={set('business_name')} />
        </div>
        <div>
          <label className="label">Contact Full Name *</label>
          <input className="input" placeholder="Maria Rivera" value={form.full_name} onChange={set('full_name')} required />
        </div>
        <div>
          <label className="label">Email Address *</label>
          <input className="input" type="email" placeholder="maria@example.com" value={form.email} onChange={set('email')} required />
        </div>
        <div>
          <label className="label">Phone</label>
          <input className="input" placeholder="(555) 123-4567" value={form.phone} onChange={set('phone')} />
        </div>
        <div>
          <label className="label">Entity Type *</label>
          <select className="input" value={form.entity_type} onChange={setEntityType} required>
            {ENTITY_TYPES.map(t => <option key={t} value={t}>{ENTITY_LABELS[t]}</option>)}
          </select>
        </div>
        <div>
          <label className="label">Tax Year</label>
          <input className="input" type="number" value={form.tax_year} onChange={set('tax_year')} min="2020" max="2030" />
        </div>
        <div>
          <label className="label">Filing Deadline</label>
          <input className="input" type="date" value={form.deadline} onChange={set('deadline')} />
        </div>
        <div>
          <label className="label">Status</label>
          <select className="input" value={form.status} onChange={set('status')}>
            {STATUSES.map(s => <option key={s} value={s}>{STATUS_LABELS[s]}</option>)}
          </select>
        </div>
        <div style={{ gridColumn: '1 / -1' }}>
          <label className="label">Internal Notes (not visible to client)</label>
          <textarea className="input" placeholder="Prior year AGI, special circumstances, etc." value={form.notes} onChange={set('notes')} rows={3} style={{ resize: 'vertical' }} />
        </div>
      </div>

      {error && <div style={{ background: '#FEE2E2', color: '#991B1B', padding: '10px 14px', borderRadius: 8, fontSize: 13, marginTop: 14 }}>{error}</div>}

      <div style={{ display: 'flex', gap: 10, marginTop: 20, justifyContent: 'flex-end' }}>
        <button type="button" className="btn btn-ghost" onClick={onClose}>Cancel</button>
        <button type="submit" className="btn btn-primary" disabled={loading}>
          {loading ? 'Saving…' : 'Add Client'}
        </button>
      </div>
    </form>
  )
}

function ClientDetail({ client, onClose, onUpdate }) {
  const navigate = useNavigate()
  const [editing, setEditing] = useState(false)
  const [form, setForm] = useState(client)
  const [loading, setLoading] = useState(false)
  const [toast, setToast] = useState('')
  const [inviteLoading, setInviteLoading] = useState(false)

  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }))
  const setEntityType = e => setForm(f => ({ ...f, entity_type: e.target.value, deadline: getDefaultDeadline(e.target.value) }))

  async function handleSave() {
    const { data: { session } } = await supabase.auth.getSession()
    if (!session) { setToast('ERROR: Not logged in'); return }
    setLoading(true)
    try {
      const res = await fetch('/.netlify/functions/update-client', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`
        },
        body: JSON.stringify({
          client_id: client.id,
          updates: {
            full_name: form.full_name,
            business_name: form.business_name,
            email: form.email,
            phone: form.phone,
            entity_type: form.entity_type,
            status: form.status,
            deadline: form.deadline || null,
            tax_year: form.tax_year,
            notes: form.notes,
            logo_url: form.logo_url || null,
            brand_color: form.brand_color || '#C9A84C',
            welcome_message: form.welcome_message || null,
          }
        })
      })
      const result = await res.json()
      if (result.error) { setToast('ERROR: ' + result.error); setTimeout(() => setToast(''), 6000) }
      else { onUpdate(result.data); setEditing(false); setToast('Saved!'); setTimeout(() => setToast(''), 2000) }
    } catch (err) {
      setToast('ERROR: ' + err.message)
      setTimeout(() => setToast(''), 6000)
    }
    setLoading(false)
  }

  async function handleExtension() {
    if (!client.deadline) {
      setToast('ERROR: No deadline set for this client')
      setTimeout(() => setToast(''), 4000)
      return
    }
    const { data: { session } } = await supabase.auth.getSession()
    if (!session) return

    // Add 6 months to current deadline
    const current = new Date(client.deadline)
    current.setMonth(current.getMonth() + 6)
    const newDeadline = current.toISOString().split('T')[0]

    setLoading(true)
    const res = await fetch('/.netlify/functions/update-client', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${session.access_token}` },
      body: JSON.stringify({
        client_id: client.id,
        updates: { deadline: newDeadline }
      })
    })
    const result = await res.json()
    if (result.error) {
      setToast('ERROR: ' + result.error)
    } else {
      onUpdate(result.data)
      setToast(`✅ Extension granted — new deadline: ${new Date(newDeadline).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`)
    }
    setTimeout(() => setToast(''), 5000)
    setLoading(false)
  }

  async function sendPortalInvite() {
    setInviteLoading(true)
    try {
      const { data: { session } } = await supabase.auth.getSession()
      const res = await fetch('/.netlify/functions/invite-client', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session?.access_token}`
        },
        body: JSON.stringify({ email: client.email, client_id: client.id, full_name: client.full_name })
      })
      const data = await res.json()
      if (data.error) {
        setToast(`Error: ${data.error}`)
      } else if (data.emailSent === false) {
        setToast('Client created, but welcome email FAILED. Share credentials manually.')
      } else {
        setToast(data.wasPasswordReset ? 'Password reset email sent to client.' : 'Client created and welcome email sent.')
      }
    } catch (err) {
      setToast('Failed to send invite')
    }
    setTimeout(() => setToast(''), 4000)
    setInviteLoading(false)
  }

  const daysUntilDeadline = client.deadline
    ? Math.ceil((new Date(client.deadline) - new Date()) / (1000 * 60 * 60 * 24))
    : null

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 20 }}>
        <div style={{
          width: 52, height: 52, borderRadius: 12,
          background: '#202326',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          color: '#CBB17B', fontWeight: 700, fontSize: 16, flexShrink: 0
        }}>
          {(client.business_name || client.full_name).split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase()}
        </div>
        <div style={{ flex: 1 }}>
          <h2 style={{ fontSize: 20, marginBottom: 2 }}>{client.business_name || client.full_name}</h2>
          <div style={{ fontSize: 13, color: '#62615D' }}>{ENTITY_LABELS[client.entity_type]} · Tax Year {client.tax_year}</div>
        </div>
        <StatusBadge status={client.status} />
      </div>

      {/* Deadline alert */}
      {daysUntilDeadline !== null && daysUntilDeadline <= 30 && daysUntilDeadline > 0 && (
        <div style={{ background: '#FEF3C7', border: '1px solid #F59E0B', borderRadius: 8, padding: '10px 14px', fontSize: 13, marginBottom: 16, fontWeight: 600 }}>
          ⚠️ Filing deadline in {daysUntilDeadline} days — {new Date(client.deadline).toLocaleDateString()}
        </div>
      )}
      {daysUntilDeadline !== null && daysUntilDeadline <= 0 && (
        <div style={{ background: '#FEE2E2', border: '1px solid #F87171', borderRadius: 8, padding: '10px 14px', fontSize: 13, marginBottom: 16, fontWeight: 600 }}>
          🚨 Deadline passed {Math.abs(daysUntilDeadline)} days ago!
        </div>
      )}

      {/* Fields */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
        {[
          ['Contact Name', 'full_name', 'text'],
          ['Business Name', 'business_name', 'text'],
          ['Email', 'email', 'email'],
          ['Phone', 'phone', 'text'],
          ['Tax Year', 'tax_year', 'number'],
          ['Filing Deadline', 'deadline', 'date'],
        ].map(([label, key, type]) => (
          <div key={key}>
            <label className="label">{label}</label>
            {editing
              ? <input className="input" type={type} value={form[key] || ''} onChange={set(key)} />
              : <div style={{ fontSize: 14, color: '#202326', padding: '8px 0' }}>{client[key] || '—'}</div>
            }
          </div>
        ))}
        <div>
          <label className="label">Entity Type</label>
          {editing
            ? <select className="input" value={form.entity_type} onChange={setEntityType}>
                {ENTITY_TYPES.map(t => <option key={t} value={t}>{ENTITY_LABELS[t]}</option>)}
              </select>
            : <div style={{ fontSize: 14, color: '#202326', padding: '8px 0' }}>{ENTITY_LABELS[client.entity_type]}</div>
          }
        </div>
        <div>
          <label className="label">Status</label>
          {editing
            ? <select className="input" value={form.status} onChange={set('status')}>
                {STATUSES.map(s => <option key={s} value={s}>{STATUS_LABELS[s]}</option>)}
              </select>
            : <div style={{ padding: '8px 0' }}><StatusBadge status={client.status} /></div>
          }
        </div>
        <div style={{ gridColumn: '1 / -1' }}>
          <label className="label">Internal Notes</label>
          {editing
            ? <textarea className="input" value={form.notes || ''} onChange={set('notes')} rows={3} style={{ resize: 'vertical' }} />
            : <div style={{ fontSize: 13, color: '#4a5568', background: '#F7F3EB', padding: '10px 12px', borderRadius: 8, minHeight: 48 }}>{client.notes || 'No notes'}</div>
          }
        </div>
      </div>

      {/* Branding */}
      {editing && (
        <div style={{ background: '#F7F3EB', borderRadius: 10, padding: '16px 18px', marginBottom: 16 }}>
          <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 12, color: '#202326' }}>🎨 Client Portal Branding</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <label className="label">Logo URL</label>
              <input className="input" placeholder="https://example.com/logo.png" value={form.logo_url || ''} onChange={set('logo_url')} />
              <div style={{ fontSize: 11, color: '#62615D', marginTop: 4 }}>Paste a direct link to the client's logo image</div>
            </div>
            <div>
              <label className="label">Brand Color</label>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <input type="color" value={form.brand_color || '#CBB17B'} onChange={set('brand_color')}
                  style={{ width: 44, height: 38, borderRadius: 6, border: '1.5px solid #DED6CA', cursor: 'pointer', padding: 2 }} />
                <input className="input" placeholder="#CBB17B" value={form.brand_color || '#CBB17B'} onChange={set('brand_color')} style={{ flex: 1 }} />
              </div>
            </div>
            <div style={{ gridColumn: '1 / -1' }}>
              <label className="label">Welcome Message</label>
              <input className="input" placeholder="Welcome! We're here to make tax season easy for you." value={form.welcome_message || ''} onChange={set('welcome_message')} />
            </div>
          </div>
        </div>
      )}

      {/* Portal invite */}
      <div style={{ background: '#F7F3EB', borderRadius: 10, padding: '14px 16px', marginBottom: 16 }}>
        <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 4 }}>Client Portal Access</div>
        <div style={{ fontSize: 12, color: '#62615D', marginBottom: 10 }}>
          {client.profile_id ? '✅ Client has portal access' : '⚠️ Client has not joined the portal yet'}
        </div>
        <button className="btn btn-primary" style={{ fontSize: 12 }} onClick={sendPortalInvite} disabled={inviteLoading}>
          {inviteLoading ? 'Sending…' : '📧 Send Portal Invite'}
        </button>
      </div>

      {/* Extension */}
      <div style={{ background: '#F1E8D7', border: '1px solid #D8C18F', borderRadius: 10, padding: '14px 16px', marginBottom: 16 }}>
        <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 4, color: '#765B2C' }}>📅 Filing Extension</div>
        <div style={{ fontSize: 12, color: '#62615D', marginBottom: 10 }}>
          {client.deadline
            ? `Current deadline: ${new Date(client.deadline).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`
            : 'No deadline set'}
        </div>
        <button className="btn btn-ghost" style={{ fontSize: 12, borderColor: '#CBB17B', color: '#765B2C' }} onClick={handleExtension} disabled={loading}>
          {loading ? 'Updating…' : '➕ Grant 6-Month Extension'}
        </button>
      </div>

      {toast && (
        <div style={{ background: toast.startsWith('ERROR') ? '#FEE2E2' : '#D1FAE5', color: toast.startsWith('ERROR') ? '#991B1B' : '#065F46', padding: '10px 14px', borderRadius: 8, fontSize: 13, marginBottom: 14, fontWeight: 600 }}>
          ✓ {toast}
        </div>
      )}

      <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
        <button className="btn btn-ghost" onClick={onClose}>Close</button>
        {editing
          ? <>
              <button className="btn btn-ghost" onClick={() => { setEditing(false); setForm(client) }}>Cancel</button>
              <button className="btn btn-primary" onClick={handleSave} disabled={loading}>{loading ? 'Saving…' : 'Save Changes'}</button>
            </>
          : <><button className="btn btn-ghost" style={{ fontSize: 12 }} onClick={() => navigate(`/clients/${client.id}/profile`)}>👤 Profile</button>
            <button className="btn btn-gold" onClick={() => setEditing(true)}>✏️ Edit Client</button></>
        }
      </div>
    </div>
  )
}

export default function ClientsPage() {
  const { profile } = useAuth()
  const [clients, setClients] = useState([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [filterStatus, setFilterStatus] = useState('all')
  const [filterEntity, setFilterEntity] = useState('all')
  const [showAdd, setShowAdd] = useState(false)
  const [selected, setSelected] = useState(null)
  const [selectedIds, setSelectedIds] = useState(new Set())
  const [bulkMode, setBulkMode] = useState(false)
  const [bulkLoading, setBulkLoading] = useState(false)
  const [showImport, setShowImport] = useState(false)
  const [importData, setImportData] = useState([])
  const [importing, setImporting] = useState(false)
  const [importResult, setImportResult] = useState('')

  useEffect(() => {
    loadClients()
  }, [])

  async function loadClients() {
    const { data } = await supabase
      .from('clients')
      .select('*')
      .eq('cpa_id', profile.id)
      .order('created_at', { ascending: false })
    setClients(data || [])
    setLoading(false)
  }

  function handleAdded(client) {
    setClients(prev => [client, ...prev])
    setShowAdd(false)
  }

  function handleUpdated(updated) {
    setClients(prev => prev.map(c => c.id === updated.id ? updated : c))
    setSelected(updated)
  }

  function toggleSelect(id) {
    setSelectedIds(prev => {
      const next = new Set(prev)
      next.has(id) ? next.delete(id) : next.add(id)
      return next
    })
  }

  function toggleSelectAll() {
    if (selectedIds.size === filtered.length) {
      setSelectedIds(new Set())
    } else {
      setSelectedIds(new Set(filtered.map(c => c.id)))
    }
  }

  async function bulkUpdateStatus(status) {
    if (selectedIds.size === 0) return
    setBulkLoading(true)
    const { data: { session } } = await supabase.auth.getSession()
    const ids = Array.from(selectedIds)

    for (const id of ids) {
      await fetch('/.netlify/functions/update-client', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${session.access_token}` },
        body: JSON.stringify({ client_id: id, updates: { status } })
      })
    }

    setClients(prev => prev.map(c => selectedIds.has(c.id) ? { ...c, status } : c))
    setSelectedIds(new Set())
    setBulkMode(false)
    setBulkLoading(false)
  }

  async function exportCSV() {
    const headers = ['Name', 'Business', 'Email', 'Phone', 'Entity Type', 'Status', 'Tax Year', 'Deadline']
    const rows = filtered.map(c => [
      c.full_name, c.business_name || '', c.email, c.phone || '',
      c.entity_type, c.status, c.tax_year, c.deadline || ''
    ])
    const csv = [headers, ...rows].map(r => r.map(v => `"${v}"`).join(',')).join('\n')
    const blob = new Blob([csv], { type: 'text/csv' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url; a.download = 'clients.csv'; a.click()
    URL.revokeObjectURL(url)
  }

  function parseCSV(text) {
    const lines = text.trim().split('\n')
    const headers = lines[0].split(',').map(h => h.replace(/"/g, '').trim().toLowerCase())
    return lines.slice(1).map(line => {
      const vals = line.split(',').map(v => v.replace(/"/g, '').trim())
      const obj = {}
      headers.forEach((h, i) => { obj[h] = vals[i] || '' })
      return {
        full_name: obj['name'] || obj['full name'] || obj['contact name'] || '',
        business_name: obj['business'] || obj['business name'] || obj['company'] || '',
        email: obj['email'] || obj['email address'] || '',
        phone: obj['phone'] || obj['phone number'] || '',
        entity_type: obj['entity type'] || obj['entity'] || '1040',
        tax_year: parseInt(obj['tax year'] || obj['year'] || new Date().getFullYear()),
        deadline: obj['deadline'] || obj['filing deadline'] || '',
        status: obj['status'] || 'not_started',
        notes: obj['notes'] || ''
      }
    }).filter(r => r.full_name || r.email)
  }

  function handleCSVFile(e) {
    const file = e.target.files[0]
    if (!file) return
    const reader = new FileReader()
    reader.onload = ev => {
      const parsed = parseCSV(ev.target.result)
      setImportData(parsed)
    }
    reader.readAsText(file)
  }

  async function handleImport() {
    if (importData.length === 0) return
    setImporting(true)
    let success = 0, failed = 0

    for (const row of importData) {
      const { data, error } = await supabase.from('clients').insert({
        ...row,
        cpa_id: profile.id,
        deadline: row.deadline || null,
        entity_type: ENTITY_TYPES.includes(row.entity_type) ? row.entity_type : '1040',
        status: STATUSES.includes(row.status) ? row.status : 'not_started',
      }).select().single()

      if (error) { failed++; continue }
      success++

      // Create organizer for each client
      await supabase.from('organizers').insert({
        client_id: data.id,
        tax_year: row.tax_year,
        entity_type: data.entity_type,
        status: 'not_started'
      })
    }

    setImportResult(`✅ Imported ${success} clients${failed > 0 ? ` (${failed} failed)` : ''}`)
    loadClients()
    setImporting(false)
  }

  const filtered = clients.filter(c => {
    const matchSearch = search === '' ||
      c.full_name?.toLowerCase().includes(search.toLowerCase()) ||
      c.business_name?.toLowerCase().includes(search.toLowerCase()) ||
      c.email?.toLowerCase().includes(search.toLowerCase())
    const matchStatus = filterStatus === 'all' || c.status === filterStatus
    const matchEntity = filterEntity === 'all' || c.entity_type === filterEntity
    return matchSearch && matchStatus && matchEntity
  })

  const daysUntil = deadline => deadline
    ? Math.ceil((new Date(deadline) - new Date()) / (1000 * 60 * 60 * 24))
    : null

  if (loading) return (
    <div style={{ display: 'grid', gap: 10 }}>
      {[...Array(5)].map((_, i) => <div key={i} className="skeleton" style={{ height: 72 }} />)}
    </div>
  )

  return (
    <div>
      {/* Toolbar */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 20, flexWrap: 'wrap', alignItems: 'center' }}>
        <input
          className="input" style={{ width: 240 }}
          placeholder="Search clients…"
          value={search} onChange={e => setSearch(e.target.value)}
        />
        <select className="input" style={{ width: 160 }} value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
          <option value="all">All Statuses</option>
          {STATUSES.map(s => <option key={s} value={s}>{STATUS_LABELS[s]}</option>)}
        </select>
        <select className="input" style={{ width: 180 }} value={filterEntity} onChange={e => setFilterEntity(e.target.value)}>
          <option value="all">All Entity Types</option>
          {ENTITY_TYPES.map(t => <option key={t} value={t}>{ENTITY_LABELS[t]}</option>)}
        </select>
        <div style={{ flex: 1 }} />
        <button className="btn btn-ghost" onClick={exportCSV} style={{ fontSize: 12 }}>⬇️ Export CSV</button>
        <button className="btn btn-ghost" onClick={() => setShowImport(true)} style={{ fontSize: 12 }}>⬆️ Import CSV</button>
        <button className="btn btn-ghost" onClick={() => { setBulkMode(!bulkMode); setSelectedIds(new Set()) }} style={{ fontSize: 12, background: bulkMode ? '#202326' : '', color: bulkMode ? '#CBB17B' : '' }}>
          {bulkMode ? '✕ Cancel' : '☑ Bulk Select'}
        </button>
        <button className="btn btn-primary" onClick={() => setShowAdd(true)}>+ Add Client</button>
      </div>

      {/* Bulk action bar */}
      {bulkMode && (
        <div style={{ background: '#202326', borderRadius: 10, padding: '12px 18px', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <button onClick={toggleSelectAll} style={{ fontSize: 12, color: '#CBB17B', background: 'none', border: '1px solid rgba(201,168,76,0.3)', borderRadius: 6, padding: '5px 12px', cursor: 'pointer', fontWeight: 600 }}>
            {selectedIds.size === filtered.length ? 'Deselect All' : 'Select All'}
          </button>
          <span style={{ color: 'rgba(255,255,255,0.5)', fontSize: 12 }}>{selectedIds.size} selected</span>
          <div style={{ flex: 1 }} />
          {selectedIds.size > 0 && (
            <>
              <span style={{ color: 'rgba(255,255,255,0.5)', fontSize: 12 }}>Mark as:</span>
              {STATUSES.map(s => (
                <button key={s} onClick={() => bulkUpdateStatus(s)} disabled={bulkLoading}
                  style={{ fontSize: 11, padding: '5px 12px', borderRadius: 6, border: 'none', cursor: 'pointer', fontWeight: 600, background: '#353A3D', color: '#CBB17B' }}>
                  {bulkLoading ? '…' : STATUS_LABELS[s]}
                </button>
              ))}
            </>
          )}
        </div>
      )}

      {/* Summary chips */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 18, flexWrap: 'wrap' }}>
        {STATUSES.map(s => {
          const count = clients.filter(c => c.status === s).length
          if (count === 0) return null
          return (
            <button key={s} onClick={() => setFilterStatus(filterStatus === s ? 'all' : s)} style={{
              padding: '4px 12px', borderRadius: 20, border: '1px solid',
              fontSize: 12, fontWeight: 600, cursor: 'pointer', transition: 'all 0.15s',
              borderColor: filterStatus === s ? '#202326' : 'var(--border)',
              background: filterStatus === s ? '#202326' : '#fff',
              color: filterStatus === s ? '#CBB17B' : '#62615D',
            }}>
              {STATUS_LABELS[s]} ({count})
            </button>
          )
        })}
      </div>

      {/* Client list */}
      {filtered.length === 0 ? (
        <div className="card" style={{ padding: '48px', textAlign: 'center' }}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>👥</div>
          <h3 style={{ marginBottom: 8 }}>{search || filterStatus !== 'all' ? 'No clients match your filters' : 'No clients yet'}</h3>
          <p style={{ color: '#62615D', fontSize: 13, marginBottom: 16 }}>
            {search || filterStatus !== 'all' ? 'Try adjusting your search or filters' : 'Add your first client to get started'}
          </p>
          {!search && filterStatus === 'all' && (
            <button className="btn btn-primary" onClick={() => setShowAdd(true)}>+ Add First Client</button>
          )}
        </div>
      ) : (
        <div style={{ display: 'grid', gap: 8 }}>
          {filtered.map(c => {
            const days = daysUntil(c.deadline)
            const urgent = days !== null && days <= 14 && days >= 0
            const overdue = days !== null && days < 0
            return (
              <div key={c.id} className="card"
                onClick={() => bulkMode ? toggleSelect(c.id) : setSelected(c)}
                style={{ padding: '14px 20px', display: 'flex', alignItems: 'center', gap: 14, cursor: 'pointer', transition: 'all 0.15s', border: `1px solid ${urgent || overdue ? '#F59E0B' : 'var(--border)'}` }}
                onMouseEnter={e => e.currentTarget.style.transform = 'translateY(-1px)'}
                onMouseLeave={e => e.currentTarget.style.transform = 'translateY(0)'}
              >
                {/* Checkbox in bulk mode */}
                {bulkMode && (
                  <div style={{
                    width: 22, height: 22, borderRadius: 6, flexShrink: 0,
                    background: selectedIds.has(c.id) ? '#CBB17B' : '#F7F3EB',
                    border: `2px solid ${selectedIds.has(c.id) ? '#CBB17B' : '#DED6CA'}`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    color: '#202326', fontWeight: 700, fontSize: 13
                  }}>
                    {selectedIds.has(c.id) ? '✓' : ''}
                  </div>
                )}

                {/* Avatar */}
                <div style={{
                  width: 42, height: 42, borderRadius: 10, flexShrink: 0,
                  background: '#202326',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  color: '#CBB17B', fontWeight: 700, fontSize: 13
                }}>
                  {(c.business_name || c.full_name).split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase()}
                </div>

                {/* Info */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 14, fontWeight: 700, color: '#202326' }}>{c.business_name || c.full_name}</div>
                  <div style={{ fontSize: 12, color: '#62615D', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {c.full_name}{c.business_name ? ` · ${c.email}` : ` · ${c.email}`}
                  </div>
                </div>

                {/* Entity */}
                <div style={{ textAlign: 'center', flexShrink: 0 }}>
                  <div style={{ fontSize: 11, color: '#62615D' }}>Entity</div>
                  <div style={{ fontSize: 12, fontWeight: 600 }}>{c.entity_type?.toUpperCase()}</div>
                </div>

                {/* Deadline */}
                <div style={{ textAlign: 'center', flexShrink: 0, minWidth: 80 }}>
                  <div style={{ fontSize: 11, color: '#62615D' }}>Deadline</div>
                  <div style={{ fontSize: 12, fontWeight: 600, color: overdue ? '#991B1B' : urgent ? '#92400E' : '#202326' }}>
                    {c.deadline ? new Date(c.deadline).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '—'}
                    {urgent && <span style={{ marginLeft: 4 }}>⚠️</span>}
                    {overdue && <span style={{ marginLeft: 4 }}>🚨</span>}
                  </div>
                </div>

                {/* Portal */}
                <div style={{ textAlign: 'center', flexShrink: 0 }}>
                  <div style={{ fontSize: 11, color: '#62615D' }}>Portal</div>
                  <div style={{ fontSize: 12 }}>{c.profile_id ? '✅' : '⏳'}</div>
                </div>

                {/* Status */}
                <StatusBadge status={c.status} />
              </div>
            )
          })}
        </div>
      )}

      {/* CSV Import modal */}
      {showImport && (
        <Modal title="Import Clients from CSV" onClose={() => { setShowImport(false); setImportData([]); setImportResult('') }}>
          <div>
            <div style={{ background: '#F7F3EB', borderRadius: 8, padding: '12px 16px', marginBottom: 16, fontSize: 13, color: '#62615D', lineHeight: 1.6 }}>
              <strong style={{ color: '#202326' }}>CSV Format:</strong> Your CSV should have these columns:<br />
              <code style={{ fontSize: 11, background: '#E8E2D9', padding: '2px 6px', borderRadius: 4 }}>Name, Business, Email, Phone, Entity Type, Tax Year, Deadline, Status, Notes</code>
              <br /><br />
              Entity Type options: <code style={{ fontSize: 11 }}>1040, 1120S, 1065, 1120, schedule_c, 990</code>
            </div>

            <div style={{ marginBottom: 16 }}>
              <label className="label">Upload CSV File</label>
              <input type="file" accept=".csv" onChange={handleCSVFile}
                style={{ width: '100%', padding: '10px', border: '1.5px dashed #DED6CA', borderRadius: 8, fontSize: 13, cursor: 'pointer' }} />
            </div>

            {importData.length > 0 && (
              <div style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8, color: '#202326' }}>Preview ({importData.length} clients found):</div>
                <div style={{ maxHeight: 200, overflow: 'auto', border: '1px solid var(--border)', borderRadius: 8 }}>
                  {importData.slice(0, 10).map((r, i) => (
                    <div key={i} style={{ padding: '8px 12px', borderBottom: '1px solid var(--border)', fontSize: 12, display: 'flex', gap: 12 }}>
                      <span style={{ fontWeight: 600, color: '#202326', flex: 1 }}>{r.full_name || r.business_name}</span>
                      <span style={{ color: '#62615D' }}>{r.email}</span>
                      <span style={{ color: '#CBB17B', fontWeight: 600 }}>{r.entity_type}</span>
                    </div>
                  ))}
                  {importData.length > 10 && (
                    <div style={{ padding: '8px 12px', fontSize: 12, color: '#62615D', textAlign: 'center' }}>+{importData.length - 10} more...</div>
                  )}
                </div>
              </div>
            )}

            {importResult && (
              <div style={{ background: '#D1FAE5', color: '#065F46', padding: '10px 14px', borderRadius: 8, fontSize: 13, marginBottom: 16, fontWeight: 600 }}>
                {importResult}
              </div>
            )}

            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button className="btn btn-ghost" onClick={() => { setShowImport(false); setImportData([]); setImportResult('') }}>Close</button>
              {importData.length > 0 && !importResult && (
                <button className="btn btn-primary" onClick={handleImport} disabled={importing}>
                  {importing ? 'Importing…' : `Import ${importData.length} Clients`}
                </button>
              )}
            </div>
          </div>
        </Modal>
      )}

      {/* Add client modal */}
      {showAdd && (
        <Modal title="Add New Client" onClose={() => setShowAdd(false)}>
          <AddClientForm onSave={handleAdded} onClose={() => setShowAdd(false)} />
        </Modal>
      )}

      {/* Client detail modal */}
      {selected && (
        <Modal title="Client Profile" onClose={() => setSelected(null)}>
          <ClientDetail client={selected} onClose={() => setSelected(null)} onUpdate={handleUpdated} />
        </Modal>
      )}
    </div>
  )
}
